// ============================================
// FILE: src/components/DuelMathGame.tsx
// ============================================
// FIXED VERSION - Proper matchmaking logic

import { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowLeft, Swords, Users, Wifi, WifiOff, X, Check, Zap } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';

interface DuelMathGameProps {
  onBack: () => void;
}

type DuelStatus = 'idle' | 'searching' | 'matched' | 'countdown' | 'playing' | 'finished' | 'opponent_disconnected';
type DifficultyLevel = 'beginner' | 'intermediate' | 'advanced';

interface Problem {
  expression: string;
  answer: number;
  index: number;
}

interface OpponentInfo {
  id: string;
  name: string;
}

interface DuelResult {
  won: boolean;
  isDraw: boolean;
  myScore: number;
  opponentScore: number;
  opponentName: string;
}

// Seeded random for consistent problems between players
class SeededRandom {
  private seed: number;
  constructor(seed: number) { this.seed = seed; }
  next(): number {
    this.seed = (this.seed * 1103515245 + 12345) & 0x7fffffff;
    return this.seed / 0x7fffffff;
  }
  nextInt(min: number, max: number): number {
    return Math.floor(this.next() * (max - min + 1)) + min;
  }
}

export default function DuelMathGame({ onBack }: DuelMathGameProps) {
  // Status
  const [status, setStatus] = useState<DuelStatus>('idle');
  const [difficulty, setDifficulty] = useState<DifficultyLevel>('intermediate');
  const [duration, setDuration] = useState(2);
  
  // User
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [currentUserName, setCurrentUserName] = useState<string>('You');
  
  // Matchmaking
  const [searchTime, setSearchTime] = useState(0);
  const [playersInLobby, setPlayersInLobby] = useState(0);
  
  // Game
  const [opponent, setOpponent] = useState<OpponentInfo | null>(null);
  const [gameId, setGameId] = useState<string | null>(null);
  const [problemSeed, setProblemSeed] = useState<number>(0);
  const [isHost, setIsHost] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [currentProblem, setCurrentProblem] = useState<Problem | null>(null);
  const [problemIndex, setProblemIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  
  // Scores
  const [myScore, setMyScore] = useState(0);
  const [myCorrect, setMyCorrect] = useState(0);
  const [myWrong, setMyWrong] = useState(0);
  const [opponentScore, setOpponentScore] = useState(0);
  const [opponentConnected, setOpponentConnected] = useState(true);
  
  // Ready states
  const [myReady, setMyReady] = useState(false);
  const [opponentReady, setOpponentReady] = useState(false);
  
  // Results
  const [duelResult, setDuelResult] = useState<DuelResult | null>(null);
  
  // Refs - USE REFS FOR VALUES NEEDED IN CALLBACKS
  const statusRef = useRef<DuelStatus>('idle');
  const currentUserIdRef = useRef<string | null>(null);
  const currentUserNameRef = useRef<string>('You');
  const matchAttemptedRef = useRef<boolean>(false);
  
  const searchIntervalRef = useRef<number | null>(null);
  const gameTimerRef = useRef<number | null>(null);
  const heartbeatRef = useRef<number | null>(null);
  const lobbyChannelRef = useRef<RealtimeChannel | null>(null);
  const gameChannelRef = useRef<RealtimeChannel | null>(null);
  const rngRef = useRef<SeededRandom | null>(null);
  const lastHeartbeatRef = useRef<number>(Date.now());

  // Keep refs in sync with state
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => {
    currentUserIdRef.current = currentUserId;
  }, [currentUserId]);

  useEffect(() => {
    currentUserNameRef.current = currentUserName;
  }, [currentUserName]);

  useEffect(() => {
    getCurrentUser();
    return () => cleanup();
  }, []);

  const cleanup = () => {
    if (searchIntervalRef.current) clearInterval(searchIntervalRef.current);
    if (gameTimerRef.current) clearInterval(gameTimerRef.current);
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    if (lobbyChannelRef.current) supabase.removeChannel(lobbyChannelRef.current);
    if (gameChannelRef.current) supabase.removeChannel(gameChannelRef.current);
  };

  const getCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      setCurrentUserId(user.id);
      currentUserIdRef.current = user.id;
      
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('name')
        .eq('id', user.id)
        .maybeSingle();
      
      if (profile?.full_name) {
        setCurrentUserName(profile.full_name);
        currentUserNameRef.current = profile.full_name;
      }
    }
  };

  // ============================================
  // MATCHMAKING (Presence-based) - FIXED
  // ============================================
  
  const startSearching = async () => {
    const userId = currentUserIdRef.current;
    if (!userId) {
      console.error('No user ID available');
      return;
    }
    
    // Reset match attempted flag
    matchAttemptedRef.current = false;
    
    setStatus('searching');
    statusRef.current = 'searching';
    setSearchTime(0);
    
    searchIntervalRef.current = window.setInterval(() => {
      setSearchTime(prev => prev + 1);
    }, 1000);
    
    const lobbyName = `matchmaking-${difficulty}-${duration}`;
    console.log('Joining lobby:', lobbyName);
    
    // Clean up any existing channel
    if (lobbyChannelRef.current) {
      await supabase.removeChannel(lobbyChannelRef.current);
    }
    
    lobbyChannelRef.current = supabase.channel(lobbyName, {
      config: { 
        presence: { key: userId },
        broadcast: { self: false }
      }
    });
    
    lobbyChannelRef.current
      .on('presence', { event: 'sync' }, () => {
        const state = lobbyChannelRef.current?.presenceState() || {};
        const playerCount = Object.keys(state).length;
        console.log('Presence sync - Players in lobby:', playerCount, state);
        setPlayersInLobby(playerCount);
        
        // Try to match if we haven't already and we're still searching
        if (playerCount >= 2 && statusRef.current === 'searching' && !matchAttemptedRef.current) {
          tryMatchPlayers(state);
        }
      })
      .on('presence', { event: 'join' }, ({ key, newPresences }) => {
        console.log('Player joined lobby:', key, newPresences);
        
        // When a new player joins, try to match
        if (statusRef.current === 'searching' && !matchAttemptedRef.current) {
          const state = lobbyChannelRef.current?.presenceState() || {};
          if (Object.keys(state).length >= 2) {
            tryMatchPlayers(state);
          }
        }
      })
      .on('broadcast', { event: 'match_request' }, ({ payload }) => {
        console.log('Received match request:', payload);
        
        // Check if this request is for us
        if (payload.targetId === currentUserIdRef.current && statusRef.current === 'searching') {
          console.log('Match request is for us! Accepting...');
          handleMatchRequest(payload);
        }
      })
      .subscribe(async (subStatus) => {
        console.log('Channel subscription status:', subStatus);
        
        if (subStatus === 'SUBSCRIBED') {
          // Track our presence
          const trackResult = await lobbyChannelRef.current?.track({
            odId: userId,
            name: currentUserNameRef.current,
            joinedAt: Date.now(),
            status: 'waiting'
          });
          console.log('Presence track result:', trackResult);
        }
      });
  };

  const tryMatchPlayers = (state: Record<string, any>) => {
    const userId = currentUserIdRef.current;
    if (!userId) {
      console.log('No user ID, cannot match');
      return;
    }
    
    // Prevent multiple match attempts
    if (matchAttemptedRef.current) {
      console.log('Match already attempted, skipping');
      return;
    }
    
    if (statusRef.current !== 'searching') {
      console.log('Not in searching state, skipping match attempt');
      return;
    }
    
    console.log('Trying to match players. Current state:', state);
    
    // Get all other waiting players
    const otherPlayers = Object.entries(state)
      .filter(([id]) => id !== userId)
      .map(([id, presences]) => {
        const presenceArray = presences as any[];
        return { id, data: presenceArray[0] };
      })
      .filter(p => p.data?.status === 'waiting')
      .sort((a, b) => (a.data?.joinedAt || 0) - (b.data?.joinedAt || 0));
    
    console.log('Other waiting players:', otherPlayers);
    
    if (otherPlayers.length === 0) {
      console.log('No other players to match with');
      return;
    }
    
    const other = otherPlayers[0];
    
    // Deterministic host selection: alphabetically lower ID is host
    const iAmHost = userId.localeCompare(other.id) < 0;
    console.log(`Host determination: My ID: ${userId}, Other ID: ${other.id}, I am host: ${iAmHost}`);
    
    if (iAmHost) {
      // Mark that we've attempted a match
      matchAttemptedRef.current = true;
      
      const seed = Math.floor(Math.random() * 1000000000);
      const newGameId = `game_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      
      console.log('I am host. Sending match request:', {
        hostId: userId,
        targetId: other.id,
        gameId: newGameId
      });
      
      // Send match request via broadcast
      lobbyChannelRef.current?.send({
        type: 'broadcast',
        event: 'match_request',
        payload: {
          hostId: userId,
          hostName: currentUserNameRef.current,
          targetId: other.id,
          gameId: newGameId,
          seed,
          difficulty,
          duration
        }
      });
      
      // Update local state
      setGameId(newGameId);
      setProblemSeed(seed);
      setIsHost(true);
      setOpponent({ id: other.id, name: other.data?.name || 'Opponent' });
      setStatus('matched');
      statusRef.current = 'matched';
      
      // Clear search interval
      if (searchIntervalRef.current) {
        clearInterval(searchIntervalRef.current);
        searchIntervalRef.current = null;
      }
      
      // Join game channel
      joinGameChannel(newGameId);
    } else {
      console.log('I am NOT host. Waiting for match request from:', other.id);
      // Non-host waits for the match_request broadcast
    }
  };

  const handleMatchRequest = (payload: any) => {
    console.log('Handling match request:', payload);
    
    // Prevent handling if we're no longer searching
    if (statusRef.current !== 'searching') {
      console.log('No longer searching, ignoring match request');
      return;
    }
    
    matchAttemptedRef.current = true;
    
    setGameId(payload.gameId);
    setProblemSeed(payload.seed);
    setIsHost(false);
    setOpponent({ id: payload.hostId, name: payload.hostName || 'Opponent' });
    setStatus('matched');
    statusRef.current = 'matched';
    
    // Clear search interval
    if (searchIntervalRef.current) {
      clearInterval(searchIntervalRef.current);
      searchIntervalRef.current = null;
    }
    
    // Leave lobby channel
    if (lobbyChannelRef.current) {
      supabase.removeChannel(lobbyChannelRef.current);
      lobbyChannelRef.current = null;
    }
    
    // Join game channel
    joinGameChannel(payload.gameId);
  };

  // ============================================
  // GAME CHANNEL (Broadcast-based)
  // ============================================
  
  const joinGameChannel = (gId: string) => {
    console.log('Joining game channel:', gId);
    
    gameChannelRef.current = supabase.channel(gId, {
      config: { broadcast: { self: false } }
    });
    
    gameChannelRef.current
      .on('broadcast', { event: 'player_ready' }, ({ payload }) => {
        console.log('Received player_ready:', payload);
        if (payload.odId !== currentUserIdRef.current) {
          setOpponentReady(true);
        }
      })
      .on('broadcast', { event: 'countdown_start' }, () => {
        console.log('Received countdown_start');
        if (statusRef.current === 'matched') {
          startCountdown();
        }
      })
      .on('broadcast', { event: 'score_update' }, ({ payload }) => {
        if (payload.odId !== currentUserIdRef.current) {
          setOpponentScore(payload.score);
        }
      })
      .on('broadcast', { event: 'heartbeat' }, ({ payload }) => {
        if (payload.odId !== currentUserIdRef.current) {
          lastHeartbeatRef.current = Date.now();
          setOpponentConnected(true);
        }
      })
      .on('broadcast', { event: 'game_complete' }, ({ payload }) => {
        console.log('Received game_complete:', payload);
        if (payload.odId !== currentUserIdRef.current) {
          finishGame(payload.finalScore);
        }
      })
      .subscribe((subStatus) => {
        console.log('Game channel subscription status:', subStatus);
      });
  };

  // Check if both players are ready
  useEffect(() => {
    if (myReady && opponentReady && status === 'matched') {
      console.log('Both players ready!');
      if (isHost) {
        console.log('I am host, sending countdown_start');
        gameChannelRef.current?.send({
          type: 'broadcast',
          event: 'countdown_start',
          payload: {}
        });
        startCountdown();
      }
    }
  }, [myReady, opponentReady, status, isHost]);

  const acceptDuel = () => {
    console.log('Accepting duel, sending player_ready');
    setMyReady(true);
    
    gameChannelRef.current?.send({
      type: 'broadcast',
      event: 'player_ready',
      payload: { odId: currentUserIdRef.current }
    });
  };

  const declineDuel = () => {
    cleanup();
    resetToIdle();
  };

  const startCountdown = () => {
    console.log('Starting countdown');
    setStatus('countdown');
    statusRef.current = 'countdown';
    setCountdown(3);
    
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          startGame();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const startGame = () => {
    console.log('Starting game with seed:', problemSeed);
    setStatus('playing');
    statusRef.current = 'playing';
    setTimeRemaining(duration * 60);
    setMyScore(0);
    setOpponentScore(0);
    setMyCorrect(0);
    setMyWrong(0);
    setProblemIndex(0);
    setUserAnswer('');
    
    rngRef.current = new SeededRandom(problemSeed);
    const firstProblem = generateProblem(rngRef.current, difficulty, 0);
    setCurrentProblem(firstProblem);
    
    gameTimerRef.current = window.setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          endGame();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    heartbeatRef.current = window.setInterval(() => {
      gameChannelRef.current?.send({
        type: 'broadcast',
        event: 'heartbeat',
        payload: { odId: currentUserIdRef.current }
      });
      
      if (Date.now() - lastHeartbeatRef.current > 5000) {
        setOpponentConnected(false);
      }
    }, 1000);
  };

  const generateProblem = useCallback((rng: SeededRandom, diff: string, idx: number): Problem => {
    const calculate = (expr: string): number => {
      const tokens = expr.replace(/×/g, '*').replace(/÷/g, '/').split(' ');
      const nums: number[] = [];
      const ops: string[] = [];
      
      for (const token of tokens) {
        if (!isNaN(Number(token))) nums.push(Number(token));
        else ops.push(token);
      }
      
      for (let i = ops.length - 1; i >= 0; i--) {
        if (ops[i] === '*' || ops[i] === '/') {
          const val = ops[i] === '*' ? nums[i] * nums[i + 1] : nums[i] / nums[i + 1];
          nums.splice(i, 2, val);
          ops.splice(i, 1);
        }
      }
      
      let result = nums[0];
      for (let i = 0; i < ops.length; i++) {
        result = ops[i] === '+' ? result + nums[i + 1] : result - nums[i + 1];
      }
      return result;
    };

    let expression: string;
    let answer: number;

    switch (diff) {
      case 'beginner': {
        const num1 = rng.nextInt(5, 25);
        const num2 = rng.nextInt(2, 12);
        const num3 = rng.nextInt(5, 20);
        const ops = ['+', '-'];
        expression = `${num1} ${ops[rng.nextInt(0, 1)]} ${num2} ${ops[rng.nextInt(0, 1)]} ${num3}`;
        answer = calculate(expression);
        break;
      }
      case 'intermediate': {
        if (rng.next() > 0.5) {
          const num1 = rng.nextInt(10, 30);
          const num2 = rng.nextInt(2, 9);
          const num3 = rng.nextInt(10, 30);
          expression = `${num1} × ${num2} ${['+', '-'][rng.nextInt(0, 1)]} ${num3}`;
        } else {
          const num1 = rng.nextInt(20, 50);
          const num2 = rng.nextInt(5, 20);
          const num3 = rng.nextInt(10, 30);
          const ops = ['+', '-'];
          expression = `${num1} ${ops[rng.nextInt(0, 1)]} ${num2} ${ops[rng.nextInt(0, 1)]} ${num3}`;
        }
        answer = Math.round(calculate(expression));
        break;
      }
      case 'advanced': {
        const patterns = [
          () => `${rng.nextInt(20, 50)} × ${rng.nextInt(2, 9)} + ${rng.nextInt(10, 20)} - ${rng.nextInt(2, 6)}`,
          () => `${rng.nextInt(40, 80)} - ${rng.nextInt(2, 8)} × ${rng.nextInt(2, 7)}`,
          () => {
            const d = rng.nextInt(2, 8);
            return `${d * rng.nextInt(5, 12)} ÷ ${d} + ${rng.nextInt(10, 30)}`;
          }
        ];
        expression = patterns[rng.nextInt(0, patterns.length - 1)]();
        answer = Math.round(calculate(expression));
        break;
      }
      default:
        expression = '10 + 5';
        answer = 15;
    }

    return { expression, answer, index: idx };
  }, []);

  const submitAnswer = () => {
    if (!currentProblem || !userAnswer) return;
    
    const userNum = parseFloat(userAnswer);
    const isCorrect = Math.abs(userNum - currentProblem.answer) < 0.1;
    
    const newScore = isCorrect ? myScore + 1 : myScore;
    if (isCorrect) {
      setMyScore(newScore);
      setMyCorrect(prev => prev + 1);
    } else {
      setMyWrong(prev => prev + 1);
    }
    
    // Broadcast score update
    gameChannelRef.current?.send({
      type: 'broadcast',
      event: 'score_update',
      payload: { odId: currentUserIdRef.current, score: newScore }
    });
    
    // Next problem
    if (rngRef.current) {
      const nextIdx = problemIndex + 1;
      setProblemIndex(nextIdx);
      setCurrentProblem(generateProblem(rngRef.current, difficulty, nextIdx));
    }
    
    setUserAnswer('');
  };

  const endGame = () => {
    console.log('Ending game');
    if (gameTimerRef.current) clearInterval(gameTimerRef.current);
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    
    gameChannelRef.current?.send({
      type: 'broadcast',
      event: 'game_complete',
      payload: { odId: currentUserIdRef.current, finalScore: myScore }
    });
    
    setTimeout(() => finishGame(opponentScore), 500);
  };

  const finishGame = async (oppFinalScore: number) => {
    console.log('Finishing game. My score:', myScore, 'Opponent score:', oppFinalScore);
    
    if (statusRef.current === 'finished') {
      console.log('Already finished, skipping');
      return;
    }
    
    setStatus('finished');
    statusRef.current = 'finished';
    
    const won = myScore > oppFinalScore;
    const isDraw = myScore === oppFinalScore;
    
    setDuelResult({
      won,
      isDraw,
      myScore,
      opponentScore: oppFinalScore,
      opponentName: opponent?.name || 'Opponent'
    });
    
    // Save to database
    const userId = currentUserIdRef.current;
    if (userId && gameId) {
      try {
        await supabase.from('duel_matches').insert({
          game_id: gameId,
          player_id: userId,
          opponent_id: opponent?.id,
          player_score: myScore,
          opponent_score: oppFinalScore,
          won,
          is_draw: isDraw,
          difficulty,
          duration_seconds: duration * 60
        });
        
        await supabase.rpc('update_duel_stats', {
          p_user_id: userId,
          p_score: myScore,
          p_won: won,
          p_is_draw: isDraw
        });
        
        console.log('Game saved to database');
      } catch (error) {
        console.error('Error saving game:', error);
      }
    }
  };

  const cancelSearch = () => {
    console.log('Cancelling search');
    cleanup();
    resetToIdle();
  };

  const resetToIdle = () => {
    setStatus('idle');
    statusRef.current = 'idle';
    setOpponent(null);
    setGameId(null);
    setDuelResult(null);
    setSearchTime(0);
    setMyReady(false);
    setOpponentReady(false);
    setOpponentConnected(true);
    matchAttemptedRef.current = false;
  };

  const handleNumberClick = (num: string) => setUserAnswer(prev => prev + num);
  const handleBackspace = () => setUserAnswer(prev => prev.slice(0, -1));
  const formatTime = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  // ============================================
  // RENDER
  // ============================================

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <header className="bg-white px-4 py-3 flex items-center justify-between sticky top-0 z-10 border-b border-gray-200">
          <button
            onClick={status === 'playing' ? undefined : (status === 'idle' ? onBack : resetToIdle)}
            disabled={status === 'playing'}
            className="w-9 h-9 rounded-xl bg-gray-100 hover:bg-gray-200 disabled:opacity-50 flex items-center justify-center"
          >
            <ArrowLeft size={18} className="text-gray-700" />
          </button>
          <div className="flex items-center gap-2">
            <span className="text-xl">⚔️</span>
            <h1 className="text-base font-bold text-gray-900">Live Duel</h1>
          </div>
          <div className="w-9 flex items-center justify-center">
            {status === 'playing' && <Wifi size={14} className="text-green-500" />}
          </div>
        </header>

        {/* IDLE STATE */}
        {status === 'idle' && (
          <div className="p-4 space-y-4">
            <div className="bg-gradient-to-br from-orange-500 via-red-500 to-pink-600 rounded-3xl p-6 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center">
                    <Swords size={28} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-black">Live Duel</h2>
                    <p className="text-white/80 text-sm">Challenge real players!</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-5 border border-gray-200">
              <div className="mb-5">
                <p className="text-xs font-bold text-gray-500 mb-3 uppercase">Duration</p>
                <div className="grid grid-cols-3 gap-2">
                  {[1, 2, 3].map(min => (
                    <button
                      key={min}
                      onClick={() => setDuration(min)}
                      className={`py-3 rounded-xl font-bold text-sm transition-all ${
                        duration === min
                          ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {min} min
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <p className="text-xs font-bold text-gray-500 mb-3 uppercase">Difficulty</p>
                <div className="space-y-2">
                  {[
                    { level: 'beginner' as DifficultyLevel, emoji: '🌱', label: 'Beginner' },
                    { level: 'intermediate' as DifficultyLevel, emoji: '⭐', label: 'Intermediate' },
                    { level: 'advanced' as DifficultyLevel, emoji: '🚀', label: 'Advanced' }
                  ].map(item => (
                    <button
                      key={item.level}
                      onClick={() => setDifficulty(item.level)}
                      className={`w-full p-3 rounded-xl flex items-center gap-3 border-2 ${
                        difficulty === item.level ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-xl">{item.emoji}</span>
                      <span className={`font-semibold ${difficulty === item.level ? 'text-red-700' : 'text-gray-900'}`}>
                        {item.label}
                      </span>
                      {difficulty === item.level && (
                        <div className="ml-auto w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                          <Check size={12} className="text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={startSearching}
                disabled={!currentUserId}
                className="w-full bg-gradient-to-r from-orange-500 via-red-500 to-pink-600 text-white py-4 rounded-xl font-bold shadow-lg flex items-center justify-center gap-3 disabled:opacity-50"
              >
                <Swords size={20} />
                Find Opponent
              </button>
            </div>
          </div>
        )}

        {/* SEARCHING STATE */}
        {status === 'searching' && (
          <div className="p-4 h-[calc(100vh-60px)] flex flex-col items-center justify-center">
            <div className="text-center">
              <div className="relative w-32 h-32 mx-auto mb-6">
                <div className="absolute inset-0 rounded-full border-4 border-red-200 animate-ping" />
                <div className="absolute inset-2 rounded-full border-4 border-orange-300 animate-ping" style={{ animationDelay: '0.2s' }} />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center shadow-lg">
                    <Users size={32} className="text-white" />
                  </div>
                </div>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Finding Opponent...</h2>
              <p className="text-gray-500 mb-4">Searching for {formatTime(searchTime)}</p>
              <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-6">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span>{playersInLobby} in lobby</span>
              </div>
              <button onClick={cancelSearch} className="px-6 py-3 rounded-xl font-bold border-2 border-gray-200 text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* MATCHED STATE */}
        {status === 'matched' && (
          <div className="p-4 h-[calc(100vh-60px)] flex flex-col items-center justify-center">
            <div className="text-center w-full max-w-sm">
              <div className="text-5xl mb-4">⚔️</div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">Opponent Found!</h2>
              
              <div className="bg-white rounded-2xl p-6 border border-gray-200 mb-6">
                <div className="flex items-center justify-between">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center text-2xl mb-2 mx-auto shadow-lg">👤</div>
                    <p className="font-bold text-gray-900 text-sm">{currentUserName.split(' ')[0]}</p>
                    <p className="text-xs text-gray-500">{myReady ? '✅ Ready' : 'You'}</p>
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center shadow-lg">
                    <span className="text-white font-black text-sm">VS</span>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center text-2xl mb-2 mx-auto shadow-lg">👤</div>
                    <p className="font-bold text-gray-900 text-sm">{opponent?.name.split(' ')[0] || 'Opponent'}</p>
                    <p className="text-xs text-gray-500">{opponentReady ? '✅ Ready' : 'Waiting...'}</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button onClick={declineDuel} className="flex-1 py-3 rounded-xl font-bold border-2 border-gray-200 text-gray-700 flex items-center justify-center gap-2">
                  <X size={18} /> Decline
                </button>
                <button
                  onClick={acceptDuel}
                  disabled={myReady}
                  className="flex-1 py-3 rounded-xl font-bold bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Check size={18} /> {myReady ? 'Waiting...' : 'Ready!'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* COUNTDOWN STATE */}
        {status === 'countdown' && (
          <div className="h-[calc(100vh-60px)] flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800">
            <div className="text-center">
              <div className="text-[120px] font-black text-white animate-pulse">{countdown}</div>
              <p className="text-white/60 text-lg">Get Ready!</p>
            </div>
          </div>
        )}

        {/* PLAYING STATE */}
        {status === 'playing' && (
          <div className="p-4 space-y-3">
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-4 text-white">
              <div className="flex items-center justify-between mb-3">
                <div className="text-center">
                  <p className="text-3xl font-black text-cyan-400">{myScore}</p>
                  <p className="text-xs text-white/60">You</p>
                </div>
                <div className="text-center">
                  <div className={`text-2xl font-black ${timeRemaining <= 10 ? 'text-red-400 animate-pulse' : 'text-white'}`}>
                    {formatTime(timeRemaining)}
                  </div>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-black text-pink-400">{opponentScore}</p>
                  <p className="text-xs text-white/60 flex items-center gap-1 justify-center">
                    {opponent?.name.split(' ')[0] || 'Opp'}
                    {!opponentConnected && <WifiOff size={12} className="text-red-400" />}
                  </p>
                </div>
              </div>
              <div className="h-2 bg-white/20 rounded-full overflow-hidden flex">
                <div className="h-full bg-cyan-400 transition-all" style={{ width: `${myScore + opponentScore > 0 ? (myScore / (myScore + opponentScore)) * 100 : 50}%` }} />
                <div className="h-full bg-pink-400 transition-all" style={{ width: `${myScore + opponentScore > 0 ? (opponentScore / (myScore + opponentScore)) * 100 : 50}%` }} />
              </div>
            </div>

            {currentProblem && (
              <div className="bg-white rounded-2xl p-5 border border-gray-200">
                <div className="text-center mb-4">
                  <div className="bg-gray-50 rounded-2xl py-6 px-4 mb-4 border border-gray-100">
                    <p className="text-3xl font-black text-gray-900">{currentProblem.expression}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 min-h-[56px] flex items-center justify-center border-2 border-gray-200">
                    <p className="text-3xl font-black text-gray-900">{userAnswer || '—'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 mb-3">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                    <button key={num} onClick={() => handleNumberClick(num.toString())} className="bg-gray-100 hover:bg-gray-200 text-gray-900 text-xl font-bold py-4 rounded-xl active:scale-95">
                      {num}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <button onClick={() => handleNumberClick('-')} className="bg-gray-100 hover:bg-gray-200 text-xl font-bold py-4 rounded-xl">−</button>
                  <button onClick={() => handleNumberClick('0')} className="bg-gray-100 hover:bg-gray-200 text-xl font-bold py-4 rounded-xl">0</button>
                  <button onClick={() => handleNumberClick('.')} className="bg-gray-100 hover:bg-gray-200 text-xl font-bold py-4 rounded-xl">.</button>
                </div>
                <div className="grid grid-cols-5 gap-2">
                  <button onClick={handleBackspace} className="col-span-2 bg-red-50 text-red-600 py-3.5 rounded-xl font-bold active:scale-95">⌫ Delete</button>
                  <button onClick={submitAnswer} disabled={!userAnswer} className="col-span-3 bg-gradient-to-r from-orange-500 to-red-500 text-white py-3.5 rounded-xl font-bold disabled:opacity-50 active:scale-95">
                    Submit →
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* FINISHED STATE */}
        {status === 'finished' && duelResult && (
          <div className="p-4 h-[calc(100vh-60px)] flex flex-col items-center justify-center">
            <div className="text-center w-full max-w-sm">
              <div className="text-6xl mb-4">
                {duelResult.isDraw ? '🤝' : duelResult.won ? '🏆' : '😔'}
              </div>
              <h2 className="text-2xl font-black text-gray-900 mb-2">
                {duelResult.isDraw ? "It's a Draw!" : duelResult.won ? 'You Won!' : 'You Lost'}
              </h2>
              
              <div className="bg-white rounded-2xl p-6 border border-gray-200 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-center">
                    <p className={`text-4xl font-black ${duelResult.won ? 'text-green-500' : 'text-gray-400'}`}>{duelResult.myScore}</p>
                    <p className="text-sm text-gray-500">You</p>
                  </div>
                  <div className="text-2xl font-bold text-gray-300">vs</div>
                  <div className="text-center">
                    <p className={`text-4xl font-black ${!duelResult.won && !duelResult.isDraw ? 'text-green-500' : 'text-gray-400'}`}>{duelResult.opponentScore}</p>
                    <p className="text-sm text-gray-500">{duelResult.opponentName}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-green-50 rounded-xl p-3 text-center">
                    <p className="text-xl font-bold text-green-600">{myCorrect}</p>
                    <p className="text-xs text-green-600">Correct</p>
                  </div>
                  <div className="bg-red-50 rounded-xl p-3 text-center">
                    <p className="text-xl font-bold text-red-600">{myWrong}</p>
                    <p className="text-xs text-red-600">Wrong</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button onClick={onBack} className="flex-1 py-3.5 rounded-xl font-bold border-2 border-gray-200 text-gray-700">Done</button>
                <button
                  onClick={() => { resetToIdle(); setTimeout(startSearching, 100); }}
                  className="flex-1 py-3.5 rounded-xl font-bold bg-gradient-to-r from-orange-500 to-red-500 text-white flex items-center justify-center gap-2"
                >
                  <Zap size={16} /> Play Again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* OPPONENT DISCONNECTED STATE */}
        {status === 'opponent_disconnected' && (
          <div className="p-4 h-[calc(100vh-60px)] flex flex-col items-center justify-center">
            <div className="text-center">
              <WifiOff size={64} className="text-gray-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">Opponent Disconnected</h2>
              <p className="text-gray-500 mb-6">Your opponent left the game</p>
              <button onClick={onBack} className="px-8 py-3.5 rounded-xl font-bold bg-gradient-to-r from-orange-500 to-red-500 text-white">
                Back to Menu
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}